import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import {LoadingButton} from "@mui/lab";
import {Box, Button, Stack} from "@mui/material";
import {alpha} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";

interface Props {
    /** Staged bars waiting for Save - the only state in which Save does anything. */
    dirty: boolean
    /** A save in flight: the actions hold still until it lands. */
    busy: boolean
    onCancel: () => void
    onSave: () => void
}

const ICON_BOX_SIZE = 36;

/**
 * The warning-tinted session bar shown while blackout editing is open: the instructions and the two
 * ways out - Cancel and Save - side by side, so the mode reads as "you are editing something that is
 * not applied yet". Entering the mode is a quiet icon button in the reply's header, not this bar.
 */
const SurveyBlackoutModeBar = ({dirty, busy, onCancel, onSave}: Props) => {
    const {t} = useLocalize();

    return (
        <Stack
            direction={'row'}
            alignItems={'center'}
            flexWrap={'wrap'}
            gap={1.5}
            role={'region'}
            aria-label={t('Blackout mode')}
            sx={theme => ({
                px: 2,
                py: 1.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: alpha(theme.palette.warning.main, 0.5),
                bgcolor: alpha(theme.palette.warning.main, 0.08),
            })}
        >
            <Box
                sx={theme => ({
                    width: ICON_BOX_SIZE,
                    height: ICON_BOX_SIZE,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.warning.main, 0.16),
                    color: 'warning.dark',
                })}
            >
                <VisibilityOffOutlinedIcon fontSize={'small'}/>
            </Box>

            <Stack gap={0.25} sx={{flex: '1 1 260px', minWidth: 0}}>
                <Stack direction={'row'} alignItems={'baseline'} flexWrap={'wrap'} gap={1}>
                    <Typography variant={'body2'} fontWeight={600} sx={{lineHeight: 1.4}}>
                        {t('Blackout mode')}
                    </Typography>

                    {dirty && (
                        <Typography
                            variant={'body2'}
                            sx={{color: 'warning.dark', fontWeight: 600, lineHeight: 1.4}}
                        >
                            {t('Unsaved changes')}
                        </Typography>
                    )}
                </Stack>

                <Typography
                    variant={'body2'}
                    color={'text.secondary'}
                    sx={{lineHeight: 1.45}}
                >
                    {t('Select part of a typed answer and confirm to black it out. Click a bar to restore it. Nothing changes until you save. Sensitive answers are shown in this mode.')}
                </Typography>
            </Stack>

            <Stack direction={'row'} alignItems={'center'} gap={1} sx={{ml: 'auto', flexShrink: 0}}>
                <Button
                    size={'small'}
                    color={'inherit'}
                    onClick={onCancel}
                    disabled={busy}
                    sx={{color: 'text.secondary'}}
                >
                    {t('Cancel')}
                </Button>

                <LoadingButton
                    size={'small'}
                    variant={'contained'}
                    onClick={onSave}
                    loading={busy}
                    disabled={!dirty}
                >
                    {t('Save')}
                </LoadingButton>
            </Stack>
        </Stack>
    );
};

export default SurveyBlackoutModeBar;
